import { isFeatureFlagEnabled } from '@onesource/components';
import { CommunityOutlined, Exercise } from '@onesource/icons';
import type {
  TFeedSpaceItem,
  TGroupedApp,
  TGroupedAppsGroup,
  TMiniProgramCatalogItem,
  TSpaceBase,
  TUserAccessMap,
} from '@onesource/schemas';
import { EAppNameEnum, sanitizeAppName, SpaceStatus, SpaceType } from '@onesource/schemas';
import {
  getSession,
  useGetAllProfileQuery,
  useGetFeedSpaces,
  useGetGroupedAppsQuery,
  useGetMiniProgramCatalogQuery,
  useGetMySpaces,
} from '@onesource/services';
import { stripHtml } from '@onesource/utils';
import { useLocation } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { RiBookOpenLine, RiChat3Line, RiTaskLine } from 'react-icons/ri';
import { ALL_APPS, appKeyToSlug, normalizeAppKey, PROGRAMS_FRONTEND_URL } from 'src/config';
import { canPreviewApp, previewToggleAtom } from 'src/features/community-hub/hooks/use-preview-mode';
import { EFeatureFlagEnum } from 'src/hooks/use-feature-flag-utils';
import { useSubmenuCollapse } from 'src/hooks/use-submenu-collapse';
import { hasAppAccess, hasCommunityAccess } from 'src/utils/app-access';

import { findPanelItem } from './find-panel-item';
import { getBottomSectionItems, getTopSectionItems, mainSectionItems, socialLinks } from './sidebar-data';
import type { SidebarItem, SidebarSection, SidebarSubItem, SocialLink } from './types';
import { useClassicAreaLink } from './use-classic-area-link';

interface UseSidebarDataReturn {
  sidebarData: SidebarSection[];
  // The menu (Programs / Community) whose submenu panel belongs to the current route
  panelItem: SidebarItem | undefined;
  socialLinks: SocialLink[];
  isLoading: boolean;
}

/**
 * Whether draft (unpublished) spaces of a given app should be surfaced — i.e. the
 * viewer both may preview that app's drafts and has the preview toggle on.
 * The sidebar spans every app, so the check is per-app rather than the
 * single-space PreviewModeProvider the space pages use.
 */
/** Slug-based program home URL (/programs/{slug}), or the legacy UUID URL (which
 * redirects) when the space has no legacy slug. Single-lesson spaces use the same
 * /programs/{slug} — the program home renders their single lesson directly. */
const programHomePath = (space: { slug?: string | null; id: string }): string =>
  space.slug ? `/programs/${appKeyToSlug(space.slug)}` : `/community-hub/spaces/${space.id}`;

type CanSeeDrafts = (app: string | null | undefined) => boolean;

/**
 * Build the nested sub-space items shown under a container space "folder".
 * Each sub-space links to the route matching its type. Sub-space types without
 * a dedicated route yet (e.g. chat) are skipped.
 *
 * Draft sub-spaces are dropped unless the viewer is previewing unpublished
 * content for this app — the `spaces` endpoint returns them to the app's
 * managers regardless of the toggle, so filtering has to happen here or a draft
 * module shows up in the menu as if it were live.
 */
const buildSubSpaceItems = (space: TSpaceBase, canSeeDrafts: CanSeeDrafts): SidebarSubItem[] => {
  const children = Array.isArray(space.children) ? space.children : [];
  const programSlug = space.slug ? appKeyToSlug(space.slug) : null;

  return children.flatMap<SidebarSubItem>(child => {
    const childSlug = child.slug ? appKeyToSlug(child.slug) : null;
    const isDraft = child.status === SpaceStatus.DRAFT;
    if (isDraft && !canSeeDrafts(child.app ?? space.app)) {
      return [];
    }

    // Progress lock, computed per user by the API. Only set when actually locked:
    // `isLocked: false` would draw an open padlock on every module row.
    const lock = child.isLocked
      ? {
          isLocked: true,
          lockMessage: child.prerequisiteName ? `Complete "${stripHtml(child.prerequisiteName)}" to unlock` : 'Locked',
        }
      : {};

    switch (child.spaceType) {
      case SpaceType.FEED: {
        // Community feeds live under /community-hub/{program}/{feed}.
        const path =
          programSlug && childSlug
            ? `/community-hub/${programSlug}/${childSlug}`
            : `/community-hub/spaces/${child.id}/community`;
        return [{ id: child.id, label: child.name, path, icon: RiChat3Line, isDraft, ...lock }];
      }
      case SpaceType.CONTENT: {
        // Modules live under /programs/{program}/{module}.
        const path =
          programSlug && childSlug
            ? `/programs/${programSlug}/${childSlug}`
            : `/community-hub/spaces/${space.id}/modules/${child.id}`;
        return [{ id: child.id, label: child.name, path, icon: RiBookOpenLine, isDraft, ...lock }];
      }
      case SpaceType.ANALYTICS: {
        // "My Analytics" shares the module route; the route branches on spaceType.
        const path =
          programSlug && childSlug
            ? `/programs/${programSlug}/${childSlug}`
            : `/community-hub/spaces/${space.id}/modules/${child.id}`;
        return [{ id: child.id, label: child.name, path, icon: RiTaskLine, isDraft, ...lock }];
      }
      default: {
        return [];
      }
    }
  });
};

// Program apps (RYC/FIT) render as app launchers, never as space folders.
const APP_CARD_IDS = new Set<string>(ALL_APPS.map(app => app.id));

interface AppSpace {
  /** The container space's own id — used for its home route. */
  spaceId: string;
  /** The space's display name from `ch_spaces.name` (not the Supabase app name). */
  name: string;
  /** The container's slug-based home path (or legacy UUID home when no slug). */
  homePath: string;
  /** Sub-space rows (Feed / Modules) shown when the folder is expanded. */
  children: SidebarSubItem[];
}

/**
 * Index community spaces by their `app` key. Every non-program space (RYC/FIT are
 * launchers, never spaces) is mapped to its id + sub-space rows, so a grouped app
 * that IS a space can either render as a collapsible folder (when it has
 * sub-spaces) or link straight to its space page (when it has none).
 */
const buildSpaceByApp = (spaces: TSpaceBase[] | undefined, canSeeDrafts: CanSeeDrafts): Map<string, AppSpace> => {
  const byApp = new Map<string, AppSpace>();
  if (!Array.isArray(spaces)) {
    return byApp;
  }

  for (const space of spaces) {
    if (!space.app || APP_CARD_IDS.has(space.app)) {
      continue;
    }
    // A draft container is unpublished as a whole — hide it (and everything under
    // it) unless the viewer is previewing this app's unpublished content.
    if (space.status === SpaceStatus.DRAFT && !canSeeDrafts(space.app)) {
      continue;
    }
    // Keyed by the normalized app key — the registry and space catalog case/format
    // the same key inconsistently, so grouped apps look it up normalized too.
    byApp.set(normalizeAppKey(space.app), {
      spaceId: space.id,
      name: space.name,
      homePath: programHomePath(space),
      children: buildSubSpaceItems(space, canSeeDrafts),
    });
  }

  return byApp;
};

/**
 * Resolve each FEED sub-space's parent container from the space catalog so the
 * Community menu can group feed spaces under their parent (the feed-spaces
 * endpoint itself doesn't carry parent info).
 */
const buildFeedParentMap = (spaces: TSpaceBase[] | undefined): Map<string, { id: string; name: string }> => {
  const parentByFeedId = new Map<string, { id: string; name: string }>();
  if (!Array.isArray(spaces)) {
    return parentByFeedId;
  }

  for (const space of spaces) {
    for (const child of space.children ?? []) {
      if (child.spaceType === SpaceType.FEED) {
        parentByFeedId.set(child.id, { id: space.id, name: space.name });
      }
    }
  }

  return parentByFeedId;
};

/**
 * Build Community menu items from FEED sub-spaces, nested under their parent
 * container as collapsible folders (feeds whose parent can't be resolved fall
 * back to flat items).
 * hasAccess is returned by the backend (getFeedSpaces endpoint) using user_access.
 */
const buildCommunityItems = (
  feedSpaces: TFeedSpaceItem[] | undefined,
  spaces: TSpaceBase[] | undefined,
  showLocked: boolean,
  isPreviewingDrafts: boolean,
): SidebarSubItem[] => {
  if (!Array.isArray(feedSpaces) || feedSpaces.length === 0) {
    return [];
  }

  const accessible = showLocked ? feedSpaces : feedSpaces.filter(s => s.hasAccess);
  // The endpoint only returns drafts to managers, but they should still be hidden
  // while the preview toggle is off so the menu matches the member's view.
  const filtered = isPreviewingDrafts ? accessible : accessible.filter(s => !s.isDraft);

  // feedId -> its slug home `/{program}/{feed}` from the space catalog (the
  // feed-spaces endpoint carries no slug/parent). Falls back to the legacy UUID
  // community route (which redirects) when a slug is missing.
  const feedSlugPathById = new Map<string, string>();
  for (const parent of spaces ?? []) {
    const parentSlug = parent.slug ? appKeyToSlug(parent.slug) : null;
    for (const child of parent.children ?? []) {
      if (child.spaceType === SpaceType.FEED && parentSlug && child.slug) {
        feedSlugPathById.set(child.id, `/community-hub/${parentSlug}/${appKeyToSlug(child.slug)}`);
      }
    }
  }

  const toFeedItem = (space: TFeedSpaceItem): SidebarSubItem => ({
    id: `community-${space.id}`,
    label: space.name,
    path: feedSlugPathById.get(space.id) ?? `/community-hub/spaces/${space.id}/community`,
    icon: RiChat3Line,
    isLocked: !space.hasAccess,
    isDraft: space.isDraft,
  });

  const parentByFeedId = buildFeedParentMap(spaces);

  // Preserve first-seen order of parent containers; collect each parent's feeds.
  const groups = new Map<string, { name: string; feeds: SidebarSubItem[] }>();
  const ungrouped: SidebarSubItem[] = [];

  for (const space of filtered) {
    const feedItem = toFeedItem(space);

    const parent = parentByFeedId.get(space.id);
    if (!parent) {
      ungrouped.push(feedItem);
      continue;
    }

    const group = groups.get(parent.id);
    if (group) {
      group.feeds.push(feedItem);
    } else {
      groups.set(parent.id, { name: parent.name, feeds: [feedItem] });
    }
  }

  const groupedItems: SidebarSubItem[] = [...groups.entries()].map(([parentId, group]) => ({
    // Group header is a non-navigable folder (the user may not have program access to the parent).
    id: `community-container-${parentId}`,
    label: group.name,
    children: group.feeds,
  }));

  return [...groupedItems, ...ungrouped];
};

/**
 * Depth-first lookup of the first navigable path in a sub-item tree — used to
 * point the top-level menu entry at a real destination when its first child is
 * a non-navigable folder.
 */
const findFirstPath = (items: SidebarSubItem[]): string | undefined => {
  for (const item of items) {
    if (item.path) {
      return item.path;
    }
    const childPath = item.children && findFirstPath(item.children);
    if (childPath) {
      return childPath;
    }
  }
  return undefined;
};

/**
 * One app row in the sidebar. Display comes from the backend grouped app; known
 * The launch target is chosen by the app's registry `type`:
 * - a community space (`spaceId` resolved from the space catalog) → its space page;
 * - a MINI program → programs-fe at `/{slug}` via OTT/SSO (targetApp PROGRAMS);
 * - otherwise a program app (RYC/FIT) with an env launch URL → OTT/SSO, or the
 *   Programs hub when it has no env URL.
 */
/**
 * The label to show for a grouped app, preferring non-Supabase sources:
 * - a community space → its `ch_spaces.name`;
 * - a known app (RYC/FIT) → common-config `ALL_APPS` (code);
 * - a MINI program → the addon mini-catalog `name`;
 * - otherwise the backend grouped-app name (Supabase `apps.name`) as a fallback.
 */
const resolveAppLabel = (
  groupedApp: TGroupedApp,
  space: AppSpace | undefined,
  miniByApp: Map<string, TMiniProgramCatalogItem>,
): string => {
  if (space) {
    return space.name;
  }
  const known = ALL_APPS.find(app => app.id === groupedApp.app);
  if (known) {
    return known.name.subscribed;
  }
  if (groupedApp.type === 'MINI') {
    const mini = miniByApp.get(normalizeAppKey(groupedApp.app));
    if (mini) {
      return mini.name;
    }
  }
  return groupedApp.name.subscribed;
};

const toAppItem = (
  groupedApp: TGroupedApp,
  space: AppSpace | undefined,
  miniByApp: Map<string, TMiniProgramCatalogItem>,
): SidebarSubItem => {
  const base = { id: groupedApp.app, label: resolveAppLabel(groupedApp, space, miniByApp) };

  // Community space → its (slug-based) space home.
  if (space) {
    return { ...base, path: space.homePath };
  }

  // MINI program → programs-fe, deep-linked to the program slug after SSO.
  if (groupedApp.type === 'MINI') {
    return {
      ...base,
      externalApp: {
        appId: EAppNameEnum.PROGRAMS,
        url: PROGRAMS_FRONTEND_URL,
        redirectTo: `/${appKeyToSlug(groupedApp.app)}`,
      },
    };
  }

  // Program app (RYC/FIT) — launch via OTT/SSO when it has an env URL; else the hub.
  const url = ALL_APPS.find(app => app.id === groupedApp.app)?.url;
  return {
    ...base,
    ...(url ? { externalApp: { appId: groupedApp.app, url: `${url}` } } : { path: '/programs' }),
  };
};

/**
 * Build sidebar app items from the backend program groups, mirroring the members
 * hub: one collapsible folder per group containing the apps the user can see
 * (has access to, or an upsell sales URL exists), respecting feature flags.
 * Only grouped apps are shown — groups with no visible app, and apps not assigned
 * to any group, are dropped.
 *
 * A grouped app the user has access to that IS a community space with sub-spaces
 * renders as a collapsible folder (its Feed / Modules nested underneath) in place
 * of its flat launcher — so spaces stay collapsible while living inside their
 * correct program group.
 *
 * Each group is emitted as a flat sequence: a non-collapsible group header
 * followed by its apps/spaces as siblings. The group itself is not a collapsible
 * parent — only its space children collapse.
 */
const buildGroupedAppItems = (
  access: TUserAccessMap | undefined,
  groups: TGroupedAppsGroup[] | undefined,
  spaceByApp: Map<string, AppSpace>,
  miniByApp: Map<string, TMiniProgramCatalogItem>,
): SidebarSubItem[] => {
  if (!access) {
    return [];
  }

  return (groups ?? []).flatMap<SidebarSubItem>(group => {
    const items = group.apps.flatMap<SidebarSubItem>(groupedApp => {
      const known = ALL_APPS.find(app => app.id === groupedApp.app);
      // Feature-flag gate (known apps only), then hub visibility: accessible or upsell.
      if (known?.featureFlagKey && !isFeatureFlagEnabled(known.featureFlagKey)) {
        return [];
      }
      const hasAccess = hasAppAccess(access, groupedApp.app);
      const isVisible = hasAccess || !!groupedApp.salesPageUrl;
      if (!isVisible) {
        return [];
      }

      // Upsell (visible only via a sales URL) → the hub, where the sales/billing
      // flow lives. Type-based launch only applies once the user has access.
      if (!hasAccess) {
        return [{ id: groupedApp.app, label: resolveAppLabel(groupedApp, undefined, miniByApp), path: '/programs' }];
      }

      // Accessible community space with sub-spaces → collapsible folder in-group;
      // otherwise route by type (space page / MINI program / program app).
      const space = spaceByApp.get(normalizeAppKey(groupedApp.app));
      if (space && space.children.length > 0) {
        return [
          {
            id: groupedApp.app,
            // Space folder shows its `ch_spaces.name`, not the Supabase app name.
            label: space.name,
            path: space.homePath,
            children: space.children,
          },
        ];
      }

      return [toAppItem(groupedApp, space, miniByApp)];
    });
    // Group header (non-collapsible) then its items — no collapsible group parent.
    return items.length > 0 ? [{ id: `app-group-${group.id}`, label: group.name, isGroupLabel: true }, ...items] : [];
  });
};

export const useSidebarData = (): UseSidebarDataReturn => {
  const token = getSession();
  const { data: profileData, isLoading } = useGetAllProfileQuery({ token });
  const { data: feedSpaces } = useGetFeedSpaces();
  const { data: mySpaces } = useGetMySpaces();
  const { data: groupedApps } = useGetGroupedAppsQuery();
  const { data: miniProgramCatalog } = useGetMiniProgramCatalogQuery();
  const { handleClassicPanelClick } = useClassicAreaLink();
  const { isSubmenuCollapsed, toggleSubmenu } = useSubmenuCollapse();
  const { pathname } = useLocation();
  const isPreviewingDrafts = useAtomValue(previewToggleAtom);

  const { sidebarData, panelItem } = useMemo(() => {
    const showLockedSpaces = isFeatureFlagEnabled(EFeatureFlagEnum.SHOW_LOCKED_SPACES);

    // Drafts show in the menu only for a manager of that app who has the
    // "Preview unpublished content" toggle on — mirroring the space pages, so
    // toggling it off gives the manager exactly the member's view.
    const canSeeDrafts: CanSeeDrafts = app => isPreviewingDrafts && canPreviewApp(profileData?.access, app);

    // Addon mini-programs keyed by the same app key the registry uses (derived
    // from the program slug), so a MINI grouped app can show its catalog name.
    const miniByApp = new Map<string, TMiniProgramCatalogItem>();
    for (const item of miniProgramCatalog ?? []) {
      miniByApp.set(normalizeAppKey(sanitizeAppName(item.slug)), item);
    }

    // Programs render as one collapsible folder per program group, driven by the
    // backend grouped-apps list, so every accessible program shows regardless of type.
    const programChildren = buildGroupedAppItems(
      profileData?.access,
      groupedApps?.groups,
      buildSpaceByApp(mySpaces, canSeeDrafts),
      miniByApp,
    );

    const spacesItem: SidebarItem = {
      id: 'programs',
      label: 'Programs',
      icon: Exercise,
      path: '/programs', // Clicking on "Programs" parent navigates to the programs page
      children: programChildren,
      // Self-labelled by the group headers, so the "Programs" panel title is hidden.
      hideTitle: true,
    };

    // Main section: Dashboard, then Programs / Community when the user has them
    const mainItems: SidebarItem[] = [...mainSectionItems];

    // Surface Programs whenever it has apps/spaces to show (in either mode).
    if (programChildren.length > 0) {
      mainItems.push(spacesItem);
    }

    // Community menu uses feed spaces — FEED sub-spaces within container spaces.
    // hasAccess is computed by the backend using user_access. Only surface the
    // menu when the user actually has access to a community space (mirrors the
    // community route guard).
    const communityChildren = buildCommunityItems(feedSpaces, mySpaces, showLockedSpaces, isPreviewingDrafts);
    if (communityChildren.length > 0 && hasCommunityAccess(feedSpaces)) {
      const communityItem: SidebarItem = {
        id: 'community',
        label: 'Community',
        icon: CommunityOutlined,
        path: findFirstPath(communityChildren),
        children: communityChildren,
      };
      mainItems.push(communityItem);
    }

    // The toggle (top section) only exists on routes that own a submenu panel —
    // Programs / Community pages. Dashboard and other pages have no panel to toggle.
    const activePanelItem = findPanelItem(mainItems, pathname);
    const topSection: SidebarSection[] = activePanelItem
      ? [
          {
            id: 'top',
            items: getTopSectionItems({ isSubmenuCollapsed, onToggleSubmenu: toggleSubmenu }),
            showDivider: true,
            desktopOnly: true,
          },
        ]
      : [];

    const sections: SidebarSection[] = [
      ...topSection,
      {
        id: 'main',
        items: mainItems,
        showDivider: true,
      },
      {
        id: 'bottom',
        items: getBottomSectionItems(() => handleClassicPanelClick()),
      },
    ];

    return { sidebarData: sections, panelItem: activePanelItem };
  }, [
    profileData,
    feedSpaces,
    mySpaces,
    groupedApps,
    miniProgramCatalog,
    handleClassicPanelClick,
    isSubmenuCollapsed,
    toggleSubmenu,
    pathname,
    isPreviewingDrafts,
  ]);

  return {
    sidebarData,
    panelItem,
    socialLinks,
    isLoading,
  };
};
